import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, CheckCircle2, Phone } from 'lucide-react';
import { supabase } from '@/lib/supabase';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Notiz {
  id: string;
  forderungsakte_id: string;
  text: string;
  telefon_dauer_sekunden: number | null;
  created_at: string;
  created_by: string | null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDateTime(d: string): string {
  return new Date(d).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDuration(sek: number): string {
  const m = Math.floor(sek / 60);
  const s = sek % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

// ─── Component ────────────────────────────────────────────────────────────────

export interface NotizenTabProps {
  forderungsAkteId: string;
}

export function NotizenTab({ forderungsAkteId }: NotizenTabProps) {
  const [notizen, setNotizen] = useState<Notiz[]>([]);
  const { session } = useAuth();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // ── Load ──────────────────────────────────────────────────────────────────

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);

    supabase
      .from('forderung_notizen')
      .select('id,forderungsakte_id,text,telefon_dauer_sekunden,created_at,created_by')
      .eq('forderungsakte_id', forderungsAkteId)
      .order('created_at', { ascending: false })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          setLoadError(error.message);
        } else {
          setNotizen((data ?? []) as Notiz[]);
        }
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [forderungsAkteId]);

  // ── Save ──────────────────────────────────────────────────────────────────

  const handleSpeichern = useCallback(async () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      const resp = await fetch('/api/forderung-add-notiz', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}) },
        body: JSON.stringify({ forderungsakte_id: forderungsAkteId, text: trimmed }),
      });
      const json = await resp.json();
      if (!resp.ok) throw new Error(json.error ?? 'Fehler beim Speichern');

      const newNotiz: Notiz = {
        id: json.notiz_id,
        forderungsakte_id: forderungsAkteId,
        text: trimmed,
        telefon_dauer_sekunden: null,
        created_at: new Date().toISOString(),
        created_by: null,
      };
      setNotizen((prev) => [newNotiz, ...prev]);
      setText('');
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err: unknown) {
      setSaveError(err instanceof Error ? err.message : 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  }, [forderungsAkteId, text]);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-4 p-4">
      {/* Eingabe */}
      <div className="space-y-2">
        <Textarea
          placeholder="Notiz eingeben…"
          value={text}
          onChange={(e) => setText(e.target.value)}
          className="min-h-[80px] resize-none text-sm"
          disabled={saving}
        />
        {saveError && <p className="text-xs text-red-600">{saveError}</p>}
        {saveSuccess && (
          <p className="flex items-center gap-1 text-xs text-green-700">
            <CheckCircle2 className="h-3.5 w-3.5" />
            Notiz gespeichert.
          </p>
        )}
        <Button
          size="sm"
          disabled={!text.trim() || saving}
          onClick={handleSpeichern}
        >
          {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Speichern
        </Button>
      </div>

      {/* Liste */}
      {loading ? (
        <div className="flex items-center justify-center py-6 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin mr-2" />
          <span className="text-sm">Lade Notizen…</span>
        </div>
      ) : loadError ? (
        <p className="text-sm text-red-600">{loadError}</p>
      ) : notizen.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-6">
          Noch keine Notizen vorhanden.
        </p>
      ) : (
        <ul className="space-y-3">
          {notizen.map((n) => (
            <li
              key={n.id}
              data-testid="notiz-item"
              className="rounded-md border bg-card p-3 text-sm space-y-1"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs text-muted-foreground">
                  {formatDateTime(n.created_at)}
                </span>
                {n.telefon_dauer_sekunden !== null && n.telefon_dauer_sekunden > 0 && (
                  <span className="flex items-center gap-1 text-xs text-green-700">
                    <Phone className="h-3 w-3" />
                    {formatDuration(n.telefon_dauer_sekunden)}
                  </span>
                )}
              </div>
              <p className="whitespace-pre-wrap break-words">{n.text}</p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
